import AbstractVideoStreamingService from 'services/video-streaming/service/abstract-video-streaming-service';
import MojitoCore from 'mojito/core';
import {
    AUGMENTED_STREAM_PROVIDER,
    STREAM_PROVIDER,
    ERROR_CODE,
} from 'services/video-streaming/types';
import ApiRequestProvider from 'services/common/api-request/request-provider.js';

const TransactionsTypes = MojitoCore.Services.Transactions.types;
const { GET } = TransactionsTypes.REQUEST_METHOD;
const logger = MojitoCore.logger.get('VideoStreamingService');

const VALIDATION_STATUS = {
    OK: 'OK',
    FAILED: 'FAILED',
};
const generateError = reason => ({
    code: ERROR_CODE.GENERIC_ERROR,
    message: `Direct provider request failed: ${reason}`,
});

/**
 * Video streaming service config.
 *
 * @typedef VideoStreamingServiceConfig
 * @type {object}
 * @property {string} serviceUrl - Streaming API endpoint URL.
 *
 * @memberof Mojito.Services.VideoStreaming
 */

/**
 * Class offering the possibility to interact with the mojito video streaming API.
 *
 * @class VideoStreamingService
 * @name service
 * @extends Mojito.Services.VideoStreaming.AbstractBetslipService
 * @memberof Mojito.Services.VideoStreaming
 */
class VideoStreamingService extends AbstractVideoStreamingService {
    constructor() {
        super();
        this.streamingUrlResolvers = {
            [STREAM_PROVIDER.IMG]: data => data?.hlsUrl,
            [STREAM_PROVIDER.PERFORM_MCC]: data => this.getLauncherUrl(data, 0),
            [AUGMENTED_STREAM_PROVIDER.BETRADAR_AUG]: data =>
                this.getLauncherUrl(data, 1) || this.getLauncherUrl(data, 0),
            [STREAM_PROVIDER.BETRADAR_AV]: data =>
                this.getLauncherUrl(data, 1) || this.getLauncherUrl(data, 0),
        };
    }

    /**
     * Configure service.
     *
     * @param {Mojito.Services.VideoStreaming.VideoStreamingServiceConfig} config - Service configuration object.
     *
     * @function Mojito.Services.VideoStreaming.service#configure
     */
    configure(config) {
        this.serviceUrl = config.serviceUrl;
        this.apiRequestFactory = new ApiRequestProvider(logger);
    }

    getStreaming(payload) {
        if (!this.validateServiceUrl()) {
            return Promise.reject();
        }
        const { provider } = payload;
        return this.requestFromAPI(payload)
            .then(data => {
                const requestPromises = data.streamInfos.map(info => {
                    return info.requestUrl
                        ? this.requestFromProvider(provider, info)
                        : Promise.resolve(info);
                });
                return Promise.allSettled(requestPromises).then(results => {
                    const isFulfilled = info => info.status === 'fulfilled';
                    const isRejected = info => info.status === 'rejected';
                    if (results.length && results.every(isRejected)) {
                        const errorInfo = results.find(isRejected);
                        throw errorInfo.reason;
                    }

                    const streamInfos = results.filter(isFulfilled).map(result => result.value);
                    return {
                        ...data,
                        streamInfos,
                    };
                });
            })
            .catch(err => {
                const { message } = err || {};
                message &&
                    logger.error(`Can not load stream data for ${provider} provider. ${message}`);
                throw err;
            });
    }

    getLauncherUrl(data, index) {
        return data?.launchInfo?.streamLauncher?.[index].launcherURL;
    }

    requestFromAPI(request) {
        return this.apiRequestFactory
            .post(`${this.serviceUrl}/video-streaming`)
            .withDescription('get video streaming')
            .withCredentials()
            .send(request);
    }

    requestFromProvider(provider, streamInfo) {
        return new Promise((resolve, reject) => {
            const validation = this.validateProvider(provider);
            if (validation.status !== VALIDATION_STATUS.OK) {
                return reject(generateError(validation.reason));
            }

            fetch(new Request(streamInfo.requestUrl, { method: GET }), {
                headers: streamInfo.headers,
            })
                .then(response => response.json())
                .then(body => {
                    const streamUrl = this.streamingUrlResolvers[provider](body);
                    if (streamUrl) {
                        resolve({ ...streamInfo, streamUrl });
                    } else {
                        reject(generateError('streamUrl is missing in provider response.'));
                    }
                })
                .catch(() => reject(generateError('Internal provider error.')));
        });
    }

    validateProvider(provider) {
        if (!this.streamingUrlResolvers[provider]) {
            return { status: VALIDATION_STATUS.FAILED, reason: `Provider is not supported.` };
        }
        return { status: VALIDATION_STATUS.OK };
    }

    validateServiceUrl() {
        if (!this.serviceUrl) {
            logger.warn('serviceUrl is missing.');
            return false;
        }
        return true;
    }
}

export default new VideoStreamingService();
